import { AnyAction } from '@reduxjs/toolkit';
import { useCallback, useState } from 'react';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { CloseButton } from '~/components/ui/CloseButton';
import { Divider } from '~/components/ui/Divider';
import { Text } from '~/components/ui/Text';
import { TextInput } from '~/components/ui/TextInput';
import { rage } from '~/rage';
import {
	IModalReplyDefault,
	ModalButtonColor,
	ModalType,
	RewardType,
	VehicleTuningCategory,
} from '~/shared';
import { HeaderContainer, Title } from '../../components/Header';
import { MultiSelect } from '../../components/MultiSelect';
import { PanelContainer } from '../../components/Panel';
import { RewardTypeSelect } from '../../components/RewardTypeSelect';
import { SearchableSelect } from '../../components/SearchableSelect';
import {
	useNumberFormat,
	usePromoValidation,
	useRewardOptions,
} from '../../hooks';
import { useLayoutDispatch, useLayoutSelector } from '../../redux/hooks';
import {
	addReward,
	removeReward,
	setCode,
	setCurrentView,
	setDateEnd,
	setDateStart,
	setIsSubmitting,
	setMaxActivations,
	setRequiredPlayTimeMin,
	setValidationErrors,
	updateReward,
} from '../../redux/slices/promoSlice';
import { store } from '../../redux/store';
import {
	formatRewardDisplay,
	getFilterPlaceholder,
	getRewardLabel,
	getRewardPlaceholder,
	getSelectPlaceholder,
	isSearchableType,
	REWARD_TYPE_LABELS,
	REWARD_TYPE_OPTIONS,
} from '../../utils/rewardHelpers';
import {
	ContentContainer,
	StyledButtonsRow,
	StyledInputGroup,
	StyledInputLabel,
	StyledLayout,
	StyledLeftColumn,
	StyledOptionalLabel,
	StyledRewardCard,
	StyledRewardDeleteButton,
	StyledRewardHeader,
	StyledRewardInfo,
	StyledRewardWrapper,
	StyledRightColumn,
	StyledSection,
	StyledSectionTitle,
	StyledValidationError,
} from './PromoAdd.styled';

const PromoAdd: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const formData = useLayoutSelector((state) => state.promo.formData);
	const validationErrors = useLayoutSelector(
		(state) => state.promo.validationErrors,
	);
	const isSubmitting = useLayoutSelector((state) => state.promo.isSubmitting);

	const { formatNumber, parseFormattedNumber, filterNumericOnly } =
		useNumberFormat();
	const {
		getValidRewards,
		getRewardValueString,
		validateForm,
		parseDateString,
	} = usePromoValidation();
	const {
		getOptionsForRewardType: getOptions,
		getTuningKitOptions,
		getTuningItemOptions,
	} = useRewardOptions();

	const [deleteButtonHover, setDeleteButtonHover] = useState<string | null>(
		null,
	);
	const [newRewardType, setNewRewardType] = useState<RewardType>(
		RewardType.MONEY,
	);
	const [newRewardValue, setNewRewardValue] = useState('');
	const [newRewardCount, setNewRewardCount] = useState('1');
	const [newRewardTuningKit, setNewRewardTuningKit] = useState('');
	const [newRewardTuningItems, setNewRewardTuningItems] = useState<string[]>(
		[],
	);

	const handleClose = useCallback(() => {
		rage.promo.exitMenu();
	}, []);

	const handleGoToList = useCallback(() => {
		dispatch(setCurrentView('list'));
	}, [dispatch]);

	const clearFieldError = useCallback(
		(field: string) => {
			if (validationErrors.some((e) => e.field === field)) {
				dispatch(
					setValidationErrors(
						validationErrors.filter((e) => e.field !== field),
					),
				);
			}
		},
		[dispatch, validationErrors],
	);

	const handleConfirmAddReward = useCallback(() => {
		let rawValue = newRewardValue;
		if (
			newRewardType === RewardType.MONEY ||
			newRewardType === RewardType.EXP
		) {
			rawValue = parseFormattedNumber(newRewardValue);
			if (!rawValue || parseInt(rawValue) === 0) return;
		}
		dispatch(
			setValidationErrors(
				validationErrors.filter((e) => !e.field.startsWith('reward_')),
			),
		);
		dispatch(addReward());
		setTimeout(() => {
			const latestRewards = store.getState().promo.formData.rewards;
			const lastReward = latestRewards[latestRewards.length - 1];
			if (lastReward) {
				dispatch(
					updateReward({
						id: lastReward.id,
						field: 'type',
						value: newRewardType,
					}),
				);
				let value: string;
				if (newRewardType === RewardType.ITEM) {
					const rawCount = parseFormattedNumber(newRewardCount);
					value = JSON.stringify({
						id: rawValue,
						count: parseInt(rawCount) || 1,
					});
				} else if (newRewardType === RewardType.VEHICLE) {
					const tuning: { kit?: string; items?: string[] } = {};
					if (newRewardTuningKit) {
						tuning.kit = newRewardTuningKit;
					}
					if (newRewardTuningItems.length > 0) {
						tuning.items = newRewardTuningItems;
					}
					value = JSON.stringify({
						model: rawValue,
						tuning: Object.keys(tuning).length > 0 ? tuning : undefined,
					});
				} else if (newRewardType === RewardType.LOOTBOX) {
					const rawCount = parseFormattedNumber(newRewardCount);
					value = JSON.stringify({
						id: rawValue,
						count: parseInt(rawCount) || 1,
					});
				} else if (newRewardType === RewardType.BUNDLE) {
					value = rawValue;
				} else {
					value = rawValue;
				}
				dispatch(updateReward({ id: lastReward.id, field: 'value', value }));
			}
		}, 0);
	}, [
		dispatch,
		newRewardType,
		newRewardValue,
		newRewardCount,
		validationErrors,
	]);

	const handleRemoveReward = useCallback(
		(id: string) => {
			rage.modal.showModal(
				{
					type: ModalType.Default,
					title: 'Подтверждение',
					text: 'Вы уверены, что хотите удалить эту награду?',
					acceptButton: { text: 'Удалить', color: ModalButtonColor.Danger },
					cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
				},
				(result) => {
					if (result.isAccepted) dispatch(removeReward(id));
				},
			);
		},
		[dispatch],
	);

	const handleConfirmSubmit = useCallback(async () => {
		dispatch(setIsSubmitting(true));
		try {
			const validRewards = getValidRewards(formData.rewards);
			const rewards = validRewards.map((r) => ({
				type: r.type,
				value: getRewardValueString(r),
			}));
			const payload = {
				code: formData.code,
				dateStart: parseDateString(formData.dateStart),
				dateEnd: parseDateString(formData.dateEnd),
				requiredPlayTimeMin: formData.requiredPlayTimeMin,
				maxActivations: formData.maxActivations
					? parseInt(formData.maxActivations)
					: null,
				rewards,
			};
			await rage.promo.createPromoCode(payload);
			rage.notifications.showSuccess('Промокод успешно создан!');
			dispatch(setCurrentView('list'));
		} catch (error: unknown) {
			rage.notifications.showError(
				error instanceof Error
					? error.message
					: 'Ошибка при создании промокода',
			);
		} finally {
			dispatch(setIsSubmitting(false));
		}
	}, [
		dispatch,
		formData,
		getValidRewards,
		getRewardValueString,
		parseDateString,
	]);

	const handleSubmit = useCallback(async () => {
		dispatch(setValidationErrors([]));
		const errors = validateForm(formData);
		if (errors.length > 0) {
			dispatch(setValidationErrors(errors));
			return;
		}
		rage.modal.showModal(
			{
				type: ModalType.Default,
				title: 'Подтверждение',
				text: `Вы действительно хотите создать промокод "${formData.code}"?`,
				acceptButton: { text: 'Создать', color: ModalButtonColor.Primary },
				cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
			},
			({ isAccepted }: IModalReplyDefault) => {
				if (isAccepted) handleConfirmSubmit();
			},
		);
	}, [dispatch, formData, validateForm, handleConfirmSubmit]);

	const getErrorForField = useCallback(
		(field: string) => validationErrors.find((e) => e.field === field)?.message,
		[validationErrors],
	);

	const isAddButtonDisabled = (): boolean => {
		const isEmptyValue = !newRewardValue.trim();
		const isZeroValue =
			(newRewardType === RewardType.MONEY ||
				newRewardType === RewardType.EXP) &&
			parseInt(parseFormattedNumber(newRewardValue)) === 0;
		return isEmptyValue || isZeroValue;
	};

	const handleInputChange =
		(dispatchAction: (value: string) => AnyAction, field: string) =>
		(value: string) => {
			dispatch(dispatchAction(value));
			clearFieldError(field);
		};

	return (
		<PanelContainer>
			<HeaderContainer>
				<Title>Создание промокода</Title>
				<Button
					color={ButtonColors.Default}
					height={ButtonHeight.Height30}
					onClick={handleGoToList}
					style={{
						position: 'absolute',
						right: 55,
						top: '50%',
						transform: 'translateY(-50%)',
						width: 'auto',
						padding: '0 12px',
					}}
				>
					<Text style={{ fontSize: 12 }}>Список</Text>
				</Button>
				<CloseButton height={ButtonHeight.Height30} onClick={handleClose} />
			</HeaderContainer>
			<ContentContainer>
				<StyledLayout>
					<StyledLeftColumn>
						<StyledSection>
							<StyledSectionTitle>Основные данные</StyledSectionTitle>
							<StyledInputGroup>
								<StyledInputLabel>Код промокода</StyledInputLabel>
								<TextInput
									value={formData.code}
									onChange={(e) =>
										handleInputChange(setCode, 'code')(e.target.value)
									}
									placeholder="WELCOME500"
									height={40}
									fontSize={16}
									error={!!getErrorForField('code')}
								/>
								{getErrorForField('code') && (
									<StyledValidationError>
										{getErrorForField('code')}
									</StyledValidationError>
								)}
							</StyledInputGroup>
							<StyledInputGroup>
								<StyledInputLabel>
									Дата начала (МСК)
									<StyledOptionalLabel>необязательно</StyledOptionalLabel>
								</StyledInputLabel>
								<TextInput
									value={formData.dateStart}
									onChange={(e) =>
										handleInputChange(setDateStart, 'dateStart')(e.target.value)
									}
									placeholder="01.01.2001 12:00"
									height={40}
									fontSize={16}
									error={!!getErrorForField('dateStart')}
								/>
								{getErrorForField('dateStart') && (
									<StyledValidationError>
										{getErrorForField('dateStart')}
									</StyledValidationError>
								)}
							</StyledInputGroup>
							<StyledInputGroup>
								<StyledInputLabel>
									Дата окончания (МСК)
									<StyledOptionalLabel>необязательно</StyledOptionalLabel>
								</StyledInputLabel>
								<TextInput
									value={formData.dateEnd}
									onChange={(e) =>
										handleInputChange(setDateEnd, 'dateEnd')(e.target.value)
									}
									placeholder="01.01.2001 12:00"
									height={40}
									fontSize={16}
									error={!!getErrorForField('dateEnd')}
								/>
								{getErrorForField('dateEnd') && (
									<StyledValidationError>
										{getErrorForField('dateEnd')}
									</StyledValidationError>
								)}
							</StyledInputGroup>
							<StyledInputGroup>
								<StyledInputLabel>
									Требуемое время игры (минут)
								</StyledInputLabel>
								<TextInput
									value={
										formData.requiredPlayTimeMin > 0
											? formatNumber(formData.requiredPlayTimeMin)
											: ''
									}
									onChange={(e) => {
										const numericOnly = filterNumericOnly(e.target.value);
										if (numericOnly && !isNaN(parseInt(numericOnly)))
											dispatch(setRequiredPlayTimeMin(parseInt(numericOnly)));
										else if (e.target.value === '')
											dispatch(setRequiredPlayTimeMin(0));
										clearFieldError('requiredPlayTimeMin');
									}}
									placeholder="60"
									height={40}
									fontSize={16}
									error={!!getErrorForField('requiredPlayTimeMin')}
									onKeyDown={(e) => {
										if (e.key === 'Enter') e.preventDefault();
									}}
								/>
								{getErrorForField('requiredPlayTimeMin') && (
									<StyledValidationError>
										{getErrorForField('requiredPlayTimeMin')}
									</StyledValidationError>
								)}
							</StyledInputGroup>
							<StyledInputGroup>
								<StyledInputLabel>
									Макс. активаций (пусто = бесконечно)
								</StyledInputLabel>
								<TextInput
									value={formData.maxActivations}
									onChange={(e) => {
										const numericOnly = filterNumericOnly(e.target.value);
										if (numericOnly && !isNaN(parseInt(numericOnly)))
											dispatch(
												setMaxActivations(formatNumber(parseInt(numericOnly))),
											);
										else if (e.target.value === '')
											dispatch(setMaxActivations(''));
										clearFieldError('maxActivations');
									}}
									placeholder="100"
									height={40}
									fontSize={16}
									error={!!getErrorForField('maxActivations')}
									onKeyDown={(e) => {
										if (e.key === 'Enter') e.preventDefault();
									}}
								/>
								{getErrorForField('maxActivations') && (
									<StyledValidationError>
										{getErrorForField('maxActivations')}
									</StyledValidationError>
								)}
							</StyledInputGroup>
						</StyledSection>
						<StyledSection style={{ marginTop: 16 }}>
							<StyledSectionTitle>Награды</StyledSectionTitle>
							{getErrorForField('rewards') && (
								<StyledValidationError style={{ marginBottom: 8 }}>
									{getErrorForField('rewards')}
								</StyledValidationError>
							)}
							<StyledRewardCard>
								<StyledInputGroup>
									<StyledInputLabel>Тип награды</StyledInputLabel>
									<RewardTypeSelect
										options={REWARD_TYPE_OPTIONS}
										value={newRewardType}
										onChange={(value) => {
											setNewRewardType(value as RewardType);
											setNewRewardValue('');
											setNewRewardCount('1');
											setNewRewardTuningKit('');
											setNewRewardTuningItems([]);
										}}
									/>
								</StyledInputGroup>
								<StyledInputGroup>
									<StyledInputLabel>
										{getRewardLabel(newRewardType)}
									</StyledInputLabel>
									{isSearchableType(newRewardType) ? (
										<SearchableSelect
											options={getOptions(newRewardType)}
											value={newRewardValue}
											onChange={(val) => {
												setNewRewardValue(val);
												if (newRewardType === RewardType.VEHICLE) {
													setNewRewardTuningKit('');
													setNewRewardTuningItems([]);
												}
											}}
											placeholder={getSelectPlaceholder(newRewardType)}
											filterPlaceholder={getFilterPlaceholder(newRewardType)}
										/>
									) : (
										<TextInput
											value={newRewardValue}
											onChange={(e) => {
												if (
													newRewardType === RewardType.MONEY ||
													newRewardType === RewardType.EXP
												) {
													const numericOnly = filterNumericOnly(e.target.value);
													if (numericOnly && !isNaN(parseInt(numericOnly))) {
														setNewRewardValue(
															formatNumber(parseInt(numericOnly)),
														);
														return;
													}
													setNewRewardValue('');
													return;
												}
												setNewRewardValue(e.target.value);
											}}
											placeholder={getRewardPlaceholder(newRewardType)}
											height={40}
											fontSize={16}
											onKeyDown={(e) => {
												if (e.key === 'Enter' && newRewardValue.trim())
													handleConfirmAddReward();
											}}
										/>
									)}
								</StyledInputGroup>
								{newRewardType === RewardType.VEHICLE && newRewardValue && (
									<>
										<StyledInputGroup style={{ marginTop: 8 }}>
											<StyledInputLabel>
												Набор тюнинга
												<StyledOptionalLabel>необязательно</StyledOptionalLabel>
											</StyledInputLabel>
											<SearchableSelect
												options={[
													{ value: '', label: 'Без набора тюнинга' },
													...getTuningKitOptions(newRewardValue),
												]}
												value={newRewardTuningKit}
												onChange={setNewRewardTuningKit}
												placeholder="Выберите набор тюнинга..."
												filterPlaceholder="Поиск набора тюнинга..."
											/>
										</StyledInputGroup>
										<StyledInputGroup style={{ marginTop: 8 }}>
											<StyledInputLabel>
												Элементы тюнинга
												<StyledOptionalLabel>необязательно</StyledOptionalLabel>
											</StyledInputLabel>
											<MultiSelect
												options={getTuningItemOptions(newRewardValue)}
												values={newRewardTuningItems}
												onChange={setNewRewardTuningItems}
												placeholder="Выберите детали..."
												filterPlaceholder="Поиск детали..."
												singleSelectCategory={VehicleTuningCategory.RIMS}
											/>
										</StyledInputGroup>
									</>
								)}
								{(newRewardType === RewardType.ITEM ||
									newRewardType === RewardType.LOOTBOX) && (
									<StyledInputGroup style={{ marginTop: 8 }}>
										<StyledInputLabel>Количество</StyledInputLabel>
										<TextInput
											value={newRewardCount}
											onChange={(e) => {
												const numericOnly = filterNumericOnly(e.target.value);
												if (numericOnly && !isNaN(parseInt(numericOnly))) {
													setNewRewardCount(
														formatNumber(parseInt(numericOnly)),
													);
													return;
												}
												setNewRewardCount('');
											}}
											placeholder="1"
											height={40}
											fontSize={16}
										/>
									</StyledInputGroup>
								)}
								<Button
									color={ButtonColors.Select}
									height={ButtonHeight.Height40}
									onClick={handleConfirmAddReward}
									disabled={isAddButtonDisabled()}
									style={{ marginTop: 8 }}
								>
									<Text>Добавить</Text>
								</Button>
							</StyledRewardCard>
						</StyledSection>
					</StyledLeftColumn>
					<Divider $direction="V" style={{ opacity: 0.1 }} />
					<StyledRightColumn>
						<StyledSection>
							<StyledSectionTitle>Добавленные награды</StyledSectionTitle>
							{formData.rewards.length === 0 ? (
								<Text
									style={{
										fontSize: 14,
										opacity: 0.5,
										color: '#ffffff',
										padding: '16px 0',
									}}
								>
									Нет добавленных наград
								</Text>
							) : (
								formData.rewards.map((reward) => (
									<StyledRewardWrapper key={reward.id}>
										<StyledRewardCard>
											<StyledRewardHeader>
												<StyledRewardInfo>
													<Text
														style={{
															fontSize: 14,
															fontWeight: 500,
															color: '#ffffff',
														}}
													>
														{REWARD_TYPE_LABELS[reward.type]}
													</Text>
													<Text
														style={{
															fontSize: 12,
															opacity: 0.7,
															color: '#ffffff',
														}}
													>
														{formatRewardDisplay(
															reward.type,
															reward.value,
															formatNumber,
														)}
													</Text>
												</StyledRewardInfo>
												<StyledRewardDeleteButton
													$hover={deleteButtonHover === reward.id}
													onClick={() => handleRemoveReward(reward.id)}
													onMouseEnter={() => setDeleteButtonHover(reward.id)}
													onMouseLeave={() => setDeleteButtonHover(null)}
												/>
											</StyledRewardHeader>
										</StyledRewardCard>
									</StyledRewardWrapper>
								))
							)}
						</StyledSection>
					</StyledRightColumn>
				</StyledLayout>
				<StyledButtonsRow style={{ marginTop: 20 }}>
					<Button
						color={ButtonColors.Default}
						height={ButtonHeight.Height50}
						onClick={handleClose}
					>
						<Text>Закрыть</Text>
					</Button>
					<Button
						color={ButtonColors.Select}
						height={ButtonHeight.Height50}
						onClick={handleSubmit}
						disabled={isSubmitting}
					>
						<Text>{isSubmitting ? 'Создание...' : 'Создать промокод'}</Text>
					</Button>
				</StyledButtonsRow>
			</ContentContainer>
		</PanelContainer>
	);
};

export { PromoAdd };
